import { useEffect, useRef } from "react";

import { isFunction, mergeDeep, time2String } from "../../utils/utils";
import useStorageState from "../useStorageState";

export default function useHeatpumpData(
  hasBattery,
  locationClient,
  showMsg = null,
  updateInterval = 60000,
  isFocused = true,
  storage = null,
) {
  const [heatpumps, saveHeatpumps, setHeatpumps] = useStorageState(
    storage,
    "heatpumps",
    null,
  );
  const initialized = useRef(false);
  const interval = useRef(null);

  const resetHeatpumps = async () => {
    try {
      const response = await locationClient.getHeatpumps(true);
      saveHeatpumps(response.data);
      return true;
    } catch (error) {
      console.warn('Error in "resetHeatpumps"', error, error.stack);
      return false;
    }
  };

  const updateHeatpumps = async () => {
    try {
      const response = await locationClient.getHeatpumps(true);
      saveHeatpumps((ws) => {
        return ws.map((w) => {
          const newData = response.data.find((_w) => _w.id === w.id) || {};
          return { ...w, ...newData };
        });
      });
      return true;
    } catch (error) {
      console.warn('Error in "updateHeatpumps"', error, error.stack);
      return false;
    }
  };

  const initData = async () => {
    await resetHeatpumps();
    initialized.current = true;
  };

  const loadData = updateHeatpumps;

  const clearData = () => {
    setHeatpumps(null);
    initialized.current = false;
  };

  useEffect(() => {
    if (isFocused) initData();
    else clearData();
  }, [isFocused]);

  useEffect(() => {
    if (initialized.current && updateInterval) {
      startRecurrentUpdate(updateInterval);
      return stopRecurrentUpdate; // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
    }
    if (!initialized.current) stopRecurrentUpdate();
  }, [initialized.current]);

  const startRecurrentUpdate = (intervalTime) => {
    if (!interval.current)
      interval.current = setInterval(() => loadData(), intervalTime);
  };

  const stopRecurrentUpdate = () => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }
  };

  const _updateHeatpumpSetPoint = (setPointId, update) => {
    saveHeatpumps((hs) =>
      hs.map((h) =>
        h._set_point?.id === setPointId
          ? mergeDeep(h, { _set_point: update })
          : h,
      ),
    );
  };

  const _updateHeatpump = (heatpumpId, update) => {
    saveHeatpumps((hs) =>
      hs.map((h) =>
        h.id === heatpumpId
          ? mergeDeep(h, isFunction(update) ? update(h) : update)
          : h,
      ),
    );
  };

  const _updateHeatpumpRequest = async (
    heatpump,
    update,
    successMsg,
    errorMsg,
  ) => {
    _updateHeatpump(heatpump.id, update); // already set to prevent flicker
    try {
      const response = await locationClient.putHeatpump(heatpump.id, update);
      _updateHeatpump(heatpump.id, response.data);
      successMsg && showMsg?.(successMsg);
      return true;
    } catch (error) {
      console.warn('Error in "_updateHeatpumpRequest"', error, error.stack);
      _updateHeatpump(heatpump.id, heatpump);
      errorMsg && showMsg?.(errorMsg, "error");
      return false;
    }
  };

  const _updateHeatpumpSetPointRequest = async (
    heatpump,
    update,
    successMsg,
    errorMsg,
  ) => {
    const setpoint = heatpump._set_point;
    _updateHeatpumpSetPoint(setpoint.id, update); // already set to prevent flicker
    try {
      const response = await locationClient.putHeatpumpSetPoint(
        setpoint.id,
        update,
      );
      _updateHeatpumpSetPoint(setpoint.id, response.data);
      successMsg && showMsg?.(successMsg);
      return true;
    } catch (error) {
      console.warn(
        'Error in "_updateHeatpumpSetPointRequest"',
        error,
        error.stack,
      );
      _updateHeatpumpSetPoint(setpoint.id, setpoint);
      errorMsg && showMsg?.(errorMsg, "error");
      return false;
    }
  };

  const handleHeatpumpNameChange = async (heatpump, name) => {
    return _updateHeatpumpRequest(
      heatpump,
      { name },
      "Name geändert.",
      "Name konnte nicht geändert werden.",
    );
  };

  const handleHeatpumpSgReadyChange = async (heatpump, sgReady) => {
    return _updateHeatpumpSetPointRequest(
      heatpump,
      { sg_ready: sgReady },
      "PV-Einschaltbefehl wurde " + (sgReady ? "aktiviert!" : "deaktiviert!"),
      "PV-Einschaltbefehl konnte nicht " +
        (sgReady ? "aktiviert!" : "deaktiviert!") +
        "werden",
    );
  };

  const handleHeatpumpSgReadyTimeChange = async (heatpump, lower, higher) => {
    return _updateHeatpumpRequest(
      heatpump,
      {
        sg_ready_start_threshold: time2String(lower),
        sg_ready_end_threshold: time2String(higher),
      },
      "Zeiten geändert.",
      "Zeiten konnten nicht geändert werden.",
    );
  };

  const handleHeatpumpSgReadySocChange = async (heatpump, soc) => {
    return _updateHeatpumpRequest(
      heatpump,
      { sg_ready_soc_threshold: soc },
      "Ladezustand geändert.",
      "Ladezustand konnte nicht geändert werden.",
    );
  };

  return {
    heatpumps,
    handleHeatpumpNameChange:
      heatpumps && heatpumps.length > 1 ? handleHeatpumpNameChange : null,
    handleHeatpumpSgReadySocChange: hasBattery
      ? handleHeatpumpSgReadySocChange
      : null,
    handleHeatpumpSgReadyTimeChange,
    handleHeatpumpSgReadyChange,
    data: { update: loadData, clear: clearData, init: initData },
    recurrentUpdate: { start: startRecurrentUpdate, stop: stopRecurrentUpdate },
  };
}
